<template>
    <div>
        <modal ref="modal"
            @hidden="$emit('stop-edit')"
        >
            <h3 slot="modal-title">
                <i class="fa fa-pencil text-warning"></i> {{resource('SprintPlanning_Edit_EditIssue_DisplayName')}}
            </h3>

            <div slot="modal-body">
                <div class="row mb-2" v-if="ready">

                    <div class="col-12 mb-2">
                        <label for="summary">{{resource('SprintPlanning_Edit_Summary_DisplayName')}}</label>
                        <textarea
                            ref="summary"
                            name="summary" 
                            class="form-control"
                            :value="issue.summary" 
                            @blur="changeSummary()"
                        />
                    </div>

                    <div class="col-12 mb-2">
                        <div class="row">
                            <div class="col-6 mb-2">
                                <label for="assignee">{{resource('SprintPlanning_Edit_AssignedTo_DisplayName')}}</label>
                                <dropdown name="assignee"
                                    :datasource="users"
                                    :value="assignee"
                                    :placeholder="'unassigned'"
                                    :textField="'displayName'"
                                    :valueField="'accountId'"
                                    @input="changeUser($event)"
                                />
                            </div>
                            <div class="col-6 mb-2">
                                <label for="status">{{resource('SprintPlanning_Edit_Status_DisplayName')}}</label>
                                <dropdown name="status"
                                    :datasource="transitions"
                                    :value="status"
                                    :textField="'name'"
                                    :valueField="'name'"
                                    @input="changeStatus($event)"
                                />
                            </div>
                        </div>
                    </div>
                    
                    <div class="col-12 mb-2" v-if="requestedByOptions && issueHasRequestedBy">
                        <div class="row">
                            <div class="col-6 mb-2">
                                <label for="requestedBy">{{resource('SprintPlanning_Issue_RequestedBy_DisplayName')}}</label>
                                <dropdown name="requestedBy"
                                    :datasource="requestedByOptions"
                                    :value="requestedBy"
                                    placeholder="-"
                                    textField="value"
                                    valueField="id"
                                    @input="changeRequestedBy"
                                />
                            </div>
                        </div>
                    </div>
                    
                    <div class="col-6 mb-2">
                        <label class="settings-label">{{resource('SprintPlanning_Edit_Urgency_DisplayName')}}</label>
                        <div class="settings-panel">
                            <div class="row">
                                <div class="col-12 mb-2">
                                    <label for="priority">{{resource('SprintPlanning_Edit_Priority_DisplayName')}}</label>
                                    <dropdown name="priority" v-if="priorities"
                                        :datasource="priorities" 
                                        :value="priority"
                                        :textField="'name'"
                                        :valueField="'id'"
                                        @input="changePriority($event)"
                                    />
                                </div>

                                <div class="col-12 mb-2" v-if="weights && issueHasWeight">
                                    <label for="weight">{{resource('SprintPlanning_Edit_Weight_DisplayName')}}</label>
                                    <dropdown name="weight"
                                        :datasource="weights" 
                                        :value="weight"
                                        :placeholder="'-'"
                                        :textField="'value'"
                                        :valueField="'id'"
                                        @input="changeWeight($event)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-6 mb-2" v-if="canEditTimeTracking">
                        <label class="settings-label">{{resource('SprintPlanning_Edit_TimeTracking_DisplayName')}}</label>
                        <div class="settings-panel">
                            <div class="row">
                                <div class="col-12 mb-2">
                                    <label for="estimated">{{resource('SprintPlanning_Edit_Estimated_DisplayName')}}</label>
                                    <jira-time-update name="estimated"
                                        :value="issue.estimatedSeconds"
                                        @input="changeEstimate($event)" 
                                    />
                                </div>

                                <div class="col-12 mb-2">
                                    <label for="remaining">{{resource('SprintPlanning_Edit_Remaining_DisplayName')}}</label>
                                    <jira-time-update name="remaining"
                                        :value="issue.remainingSeconds"
                                        @input="changeRemaining($event)" 
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 mb-2" v-if="canAddComments">
                        <comment @input="changeComment($event)" />
                    </div>

                </div>
            </div>

            <div slot="modal-footer">
                <button @click="updateIssue()" class="btn btn-primary">
                    <i class="fa fa-save"></i> {{resource('Button_Save')}}
                </button>
            </div>

        </modal>
    </div>
</template>

<script>

//Util
import api from '../api';
import {secondsToJiraTimeString as timeString} from '../../../Util/time';
import jira from '../Util/jira';

//Mixins
import resourceMixin from '../../../Util/Mixins/resources';
import apiMixin from '../Mixins/edit-api';

//Componenets
import Modal from '../../../Components/Util/Modal.vue';
import Dropdown from '../../../Components/Inputs/Dropdown.vue';
import JiraTimeUpdate from './JiraTimeUpdate.vue';
import Comment from './Comment.vue';

export default {
    name: 'sprint-planning-edit',
    mixins: [resourceMixin, apiMixin],
    components: {
        Modal,
        Dropdown,
        JiraTimeUpdate,
        Comment
    },
    created() {
        const vm = this;
        
        api.validateTimeTrackingWritePermission()
            .then(canWrite => vm.canEditTimeTracking = canWrite);
        
        api.validateCommentsWritePermission()
            .then(canWrite => vm.canAddComments = canWrite);
        
        vm.getIssueMetaData(vm.issue);
    },
    methods: {
        changeSummary() {
            const summary = this.$refs.summary.value;
            this.issueModel.summary = summary || null;
        },
        changeUser(accountId) {
            const user = this.users.find((user) => user.accountId === accountId);
            this.issueModel.assignee = user || null;
        },
        changeWeight(id) {
            const weight = this.weights.find((weight) => weight.id === id);
            this.issueModel.weight = weight || null;
        },
        changeRequestedBy(id) {
            const requestedBy = this.requestedByOptions.find(requestedBy => requestedBy.id === id);
            this.issueModel.requestedBy = requestedBy || null;
        },
        changePriority(id) {
            const priority = this.priorities.find((priority) => priority.id === id);
            this.issueModel.priority = priority;
        },
        changeStatus(name) {
            const transition = this.transitions.find((transitions) => transitions.name === name);
            this.issueModel.status = transition;
        },
        changeEstimate(value) {
            this.issueModel.estimatedSeconds = value;
            this.issueModel.estimated = timeString(value);
        },
        changeRemaining(value) {
            this.issueModel.remainingSeconds = value;
            this.issueModel.remaining = timeString(value);
        },
        changeComment(comment) {
            comment !== '' ?
                this.issueModel.comment = comment :
                this.issueModel.comment = undefined;
        },
    },
    computed: {
        hasIssue() {
            return this.issue != null;
        },
        assignee() {
            return this.issue.assignee != null ? this.issue.assignee.accountId : null;
        },
        priority() {
            return this.issue.priority != null ? this.issue.priority.id : null;
        },
        weight() {
            return this.issue.weight != null ? this.issue.weight.id : null;
        },
        requestedBy() {
            return this.issue.requestedBy != null ? this.issue.requestedBy.id : null;
        },
        status() {
            return this.issue.status != null ? this.issue.status.name : null;
        },
        issueHasWeight() {
            const fieldNames = this.customFields.map((field) => { 
                return field.key;
            });

            return fieldNames.indexOf(jira.fields.weight.name) !== -1;
        },
        issueHasRequestedBy(){
            const requestedByField = this.customFields.find(
                field => field.key === jira.fields.requestedBy.name
            );
            
            return requestedByField !== undefined;
        }
    },
    watch: {
        ready() {
            this.$refs.modal.show();
        }
    },
    data() {
        return {
            issueModel: {},
            canEditTimeTracking: false,
            canAddComments: false,
            ready: false
        };
    },
    props: {
        issue: {
            type: Object,
            default: null
        },
        users: {
            type: Array,
            required: true
        }
    }
};
</script>

<style lang="scss" scoped src='../Style/edit.scss'>

</style>
